<template>
  <v-tooltip
    class="playposit-tooltip"
    v-model="showTooltip"
    :top="top"
    :bottom="bottom"
    :left="left"
    :right="right"
    :open-on-hover="false"
    :disabled="disabled"
    :tag="tag"
    :content-class="contentClass"
    :attach="attach"
    :z-index="zIndex"
    :max-width="maxWidth"
  >
    <template v-slot:activator="{}">
      <slot></slot>
    </template>
    <span
      class="white--text"
      @mouseover="enableTooltip"
      @mouseleave="disableTooltipWithDelay"
    >
      <slot name="text"></slot>
    </span>
  </v-tooltip>
</template>

<script>
import EventBus from '../js/eventBus';

export default {
  name: 'Tooltip',
  props: {
    top: Boolean,
    bottom: Boolean,
    left: Boolean,
    right: Boolean,
    disabled: Boolean,
    contentClass: String,
    attach: String,
    zIndex: [Number, String],
    tag: {
      type: String,
      default: 'span',
    },
    delayTime: {
      type: Number,
      default: 700,
    },
    maxWidth: {
      type: [Number, String],
      default: '100%',
    },
  },
  data: () => ({
    showTooltip: false,
    hoverTimer: null,
    activationElement: null,
  }),
  methods: {
    enableTooltip() {
      if (this.disabled) return;
      this.showTooltip = true;
      document.addEventListener('keydown', this.handleEscapeKey);
      clearTimeout(this.hoverTimer);
      EventBus.$emit('tooltipEnabled', this);
    },
    disableTooltip() {
      this.showTooltip = false;
      document.removeEventListener('keydown', this.handleEscapeKey);
      clearTimeout(this.hoverTimer);
    },
    disableTooltipWithDelay() {
      this.hoverTimer = setTimeout(this.disableTooltip, this.delayTime);
    },
    handleEscapeKey(event) {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      this.disableTooltip();
    },
    initEventListeners() {
      /*
        Hovering over two or more Tooltip components that are close together will result in the tooltips overlapping. This EventBus listener makes it so that whenever a tooltip is opened, all other ones are closed.
      */
      EventBus.$on('tooltipEnabled', (sourceTooltipComponent) => {
        if (
          this.showTooltip
          && sourceTooltipComponent !== this
        ) this.disableTooltip();
      });

      /*
        This sets `this.activationElement` to the first element within the default slot of this component, which should be whatever element triggers the tooltip.
      */
      this.activationElement = this.$slots.default[0].elm;
      if (!this.activationElement) return;

      /*
        Assigning events through `addEventListener()` instead of adding `@mouseenter` onto the element allows us to attach these events directly to the slotted activation element instead of having to attach them to a <div> that wraps the slot.
      */
      this.activationElement.addEventListener('mouseenter', this.enableTooltip);
      this.activationElement.addEventListener('focus', this.enableTooltip);
      this.activationElement.addEventListener('mouseleave', this.disableTooltipWithDelay);
      this.activationElement.addEventListener('blur', this.disableTooltipWithDelay);
    },
  },
  watch: {
    disabled(n) {
      if (n) this.disableTooltip();
    },
    showTooltip(n) {
      this.$emit('change', n);
    },
  },
  beforeDestroy() {
    if (!this.activationElement) return;
    this.activationElement.removeEventListener('mouseenter', this.enableTooltip);
    this.activationElement.removeEventListener('focus', this.enableTooltip);
    this.activationElement.removeEventListener('mouseleave', this.disableTooltipWithDelay);
    this.activationElement.removeEventListener('blur', this.disableTooltipWithDelay);
  },
  async mounted() {
    await this.$nextTick();
    this.initEventListeners();
  },
};
</script>

<style>
  /*
    This is a shell element that needs to be hidden or else it will interfere with flex/grid layouts.
  */
  .v-tooltip.playposit-tooltip {
    display: none;
  }
</style>